/**
 * Chip cloud with live client-side filtering — the Tags tab, and the Folders
 * tab, which is the same thing over a different list.
 *
 * The full list comes from the server once; typing in the filter box narrows
 * it locally (no request per keystroke). Chip weight is bucketed by relative
 * count so a handful of dominant entries don't flatten everything else.
 */

import { el, replaceChildren } from "../lib/dom";
import { highlightSubstring } from "../lib/highlight";

const WEIGHT_BUCKETS = 5;

export interface ChipItem {
  readonly label: string;
  readonly count: number;
}

export interface ChipCloudOptions {
  readonly items: readonly ChipItem[];
  readonly selected: readonly string[];
  /** Plural noun for the summary and filter placeholder: "tags", "folders". */
  readonly noun: string;
  readonly onToggle: (label: string) => void;
}

export interface ChipCloudHandle {
  readonly element: HTMLElement;
  /** Repaints selection state without losing the filter text. */
  readonly setSelected: (labels: readonly string[]) => void;
}

function weightOf(count: number, maxCount: number): number {
  if (maxCount <= 0) {
    return 1;
  }
  const ratio = Math.sqrt(count) / Math.sqrt(maxCount);
  return Math.min(WEIGHT_BUCKETS, Math.max(1, Math.ceil(ratio * WEIGHT_BUCKETS)));
}

export function chipCloud(options: ChipCloudOptions): ChipCloudHandle {
  const maxCount = options.items.reduce((highest, entry) => Math.max(highest, entry.count), 0);
  let selected = new Set(options.selected);

  const chips = el("div", { className: "tag-cloud__chips" });
  const summary = el("p", { className: "tag-cloud__summary" });

  const filterInput = el("input", {
    className: "input input--filter",
    attrs: {
      type: "search",
      placeholder: `Filter ${options.noun}…`,
      "aria-label": `Filter ${options.noun}`,
      autocomplete: "off",
    },
  });

  const paint = (): void => {
    const needle = filterInput.value.trim().toLowerCase();
    const matches = options.items.filter((entry) => needle === "" || entry.label.toLowerCase().includes(needle));

    if (matches.length === 0) {
      replaceChildren(chips, el("p", { className: "state__detail", text: `No ${options.noun} match that filter.` }));
    } else {
      replaceChildren(
        chips,
        ...matches.map((entry) => {
          const isSelected = selected.has(entry.label);
          return el("button", {
            className: `tag-chip tag-chip--w${weightOf(entry.count, maxCount)}${isSelected ? " tag-chip--selected" : ""}`,
            attrs: { type: "button", "aria-pressed": isSelected, title: `${entry.count} notes` },
            children: [
              el("span", { className: "tag-chip__label", children: [highlightSubstring(entry.label, needle)] }),
              el("span", { className: "tag-chip__count", text: String(entry.count) }),
            ],
            on: {
              click: (): void => {
                options.onToggle(entry.label);
              },
            },
          });
        }),
      );
    }

    summary.textContent =
      matches.length === options.items.length
        ? `${options.items.length} ${options.noun}`
        : `${matches.length} of ${options.items.length} ${options.noun}`;
  };

  filterInput.addEventListener("input", paint);
  paint();

  const element = el("section", {
    className: "tag-cloud",
    children: [el("div", { className: "tag-cloud__controls", children: [filterInput, summary] }), chips],
  });

  return {
    element,
    setSelected(labels: readonly string[]): void {
      selected = new Set(labels);
      paint();
    },
  };
}
